import {resolveBackground, setAlpha} from '#core/utils/color-utils.js';
import {absoluteUrlToInternalResource} from '#core/utils/url-utils.js';

import {PALETTE_IDS} from '#core/application/modules/ids.js';

const COLOR = {
    SCRIM_LIGHT: 'rgba(0, 0, 0, 0.6)',
    SCRIM_DARK: 'rgba(0, 0, 0, 0.8)',
    SHADOW_SHORT: 'rgba(0, 0, 0, 0.3)',
    SHADOW_LONG: 'rgba(0, 0, 0, 0.5)',

    // Colors from brandbook
    BRAND_BLACK: '#1a181b', //#202020
    BRAND_WHITE: '#f8f8f0', // #fcfcfe
    BRAND_VIOLET1: '#6a46f3', // text and graphics
    BRAND_VIOLET2: '#c325b4', // accent
    BRAND_BLUE1: '#10c5de', // accent
    BRAND_DARK_GRAY: '#414149', // BG
    BRAND_LIGHT_GRAY: '#eaeaf0', // BG
    BRAND_ORANGE: '#F6AE2D',

    // Custom colors
    CUSTOM_DIVIDER: '#4456',
    CUSTOM_BG_GRAY1: '#242226',
    CUSTOM_BG_GRAY2: '#2b2b2d',
    CUSTOM_BG_GRAY3: '#D9D9D9',
    CUSTOM_BG_GRAY4: '#5B5B5B',
    CUSTOM_TEXT_GRAY: '#818194',
    CUSTOM_TEXT_VIOLET: '#957bff',

    CUSTOM_BORDER_LIGHT: '#6b6b80',
    CUSTOM_BORDER_DARK: '#2c2b32',
    GRADIENT: `linear-gradient(to right bottom, #6a46f3, #c325b4)`, //#b829bc
    GRADIENT_DARK: `linear-gradient(to right bottom, #242226, #1a181b)`,
    GRADIENT_ACCENT: 'linear-gradient(90deg, #F5AC2F 0%, #C325B4 100%)',
    GRADIENT_RADIAL: `radial-gradient(ellipse at 30% 60% in lch shorter hue, #c325b420, transparent 60%), radial-gradient(ellipse at 70% 40% in oklch shorter hue, #6a46f320, transparent 60%)`,
    GRADIENT_HORIZONTAL: `linear-gradient(to right, #6a46f3, #c325b4)`,
    SVG_PURPLE_PINK_GRADIENT: 'url(#purple-pink-gradient)',
    GRADIENT_GRAY: `linear-gradient(to right bottom, #2C2B30, #414149)`,
};

const FONT = {
    FAMILY: 'NeusaNeuVT, sans-serif',

    WEIGHT_THIN: 300,
    WEIGHT_NORMAL: 400,
    WEIGHT_SEMI_BOLD: 500,
    WEIGHT_BOLD: 700,

    SIZE_XXSMALL: 12,
    SIZE_XSMALL: 12,
    SIZE_SMALL: 13,
    SIZE_MEDIUM: 14,
    SIZE_LARGE: 16,
    SIZE_XLARGE: 18,
};

export const THEME_DARK = {
    [PALETTE_IDS.BASE]: {
        FONT_FAMILY: 'NeusaNeuVT, sans-serif',
        VERTICAL_ALIGN_FIX: 4,

        FILL_PAGE: COLOR.CUSTOM_BG_GRAY1,
        FILL_HEADER: COLOR.BRAND_DARK_GRAY,
        FILL_HERO: COLOR.BRAND_BLACK,
        FILL_SHEET: COLOR.BRAND_DARK_GRAY,
        FILL_POPOVER: COLOR.BRAND_BLACK,

        FILL_CONTENT_DEFAULT: COLOR.BRAND_DARK_GRAY,
        FILL_CONTENT_PROMINENT: COLOR.GRADIENT_DARK,

        FILL_CARD: COLOR.BRAND_DARK_GRAY,
        FILL_CARD_PROMINENT: COLOR.GRADIENT_GRAY,

        FILL_CONTROL_BACKGROUND: '#123456',
        FILL_CONTROL_FILL: '#123456',
        FILL_CONTROL_OUTLINE: COLOR.CUSTOM_BORDER_LIGHT,
        FILL_CONTROL_FOREGROUND: '#123456',
        FILL_CONTROL_ACCENT: COLOR.BRAND_VIOLET1,
        FILL_CONTROL_ODDS: COLOR.CUSTOM_BG_GRAY1,

        FILL_ACCENT_PRIMARY: COLOR.BRAND_VIOLET1,
        FILL_ACCENT_SECONDARY: COLOR.BRAND_BLUE1,
        FILL_ACCENT_FOCUS: COLOR.BRAND_VIOLET2,

        FILL_SELECTED_PRIMARY: COLOR.BRAND_VIOLET1,
        FILL_SELECTED_ODDS: COLOR.BRAND_BLUE1,

        DEFAULT_PRIMARY_TEXT: `${COLOR.BRAND_WHITE}|${COLOR.BRAND_BLACK}`,
        DEFAULT_SECONDARY_TEXT: `${setAlpha(COLOR.BRAND_WHITE, 0.75)}|${setAlpha(COLOR.BRAND_BLACK, 0.75)}`,
        TEXT_DEFAULT_TERTIARY: '#5A5A6B',

        TEXT_PROMINENT_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_PROMINENT_SECONDARY: '#5A5A6B',
        TEXT_HERO_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_HERO_SECONDARY: '#5A5A6B',

        TEXT_ODDS_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_ODDS_SECONDARY: '#5A5A6B',

        TEXT_ACCENT_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_ACCENT_SECONDARY: COLOR.BRAND_BLUE1,
        TEXT_ACCENT_FILL: COLOR.BRAND_BLUE1,

        TEXT_SELECTED_PRIMARY: COLOR.BRAND_WHITE,
        TEXT_SELECTED_ODDS: COLOR.BRAND_WHITE,

        DEFAULT_GENERIC_ACCENT_COLOR: COLOR.BRAND_BLUE1,
        DEFAULT_DIVIDER: COLOR.CUSTOM_DIVIDER,

        FILL_SEMANTIC_ERROR: '#dd3054',
        FILL_SEMANTIC_SUCCESS: '#1bc37c',
        FILL_SEMANTIC_WARN: '#a5835f',

        DEFAULT_BUTTON_BORDER_RADIUS: 2,
        DEFAULT_DROPDOWN_BORDER_RADIUS: 2,
        DEFAULT_SHEET_BORDER_RADIUS: 2,
        DEFAULT_CARD_BORDER_RADIUS: 2,

        SHADOW_SHORT: '0 1px 10px 0 rgba(0, 0, 0, 0.15)',
        SHADOW_LONG: '0 2px 10px 0 rgba(0, 0, 0, 0.15)',
        DEFAULT_BOX_DECORATION_WITH_SHADOW: false,

        DEFAULT_TILE_COLOR_SCHEME: 'none',
    },
    // Components ============================
    [PALETTE_IDS.ODDS_BUTTON]: {
        ODDS_PRICE_TEXT: {
            color: COLOR.CUSTOM_TEXT_VIOLET,
            fontWeight: FONT.WEIGHT_BOLD,
        },
        ODDS_INFO_TEXT: {
            color: COLOR.BRAND_WHITE,
            fontSize: FONT.SIZE_XSMALL,
        },
    },
    [PALETTE_IDS.PROGRESS_BAR]: {
        PROGRESS_BAR_LINE_COLOR: COLOR.CUSTOM_BG_GRAY4,
    },
    [PALETTE_IDS.PIVOT]: {
        PIVOT_TEXT: {
            color: COLOR.BRAND_WHITE,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
        },
        PIVOT_SELECTED_TEXT: {
            color: COLOR.CUSTOM_TEXT_VIOLET,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
        },
        PIVOT_INDICATOR_BG: COLOR.CUSTOM_TEXT_VIOLET,
        PIVOT_INDICATOR_HEIGHT: 2,
        PIVOT_INDICATOR_BOTTOM: 2,
    },
    // [PALETTE_IDS.INPUT]: {
    //     INPUT_BG: COLOR.CUSTOM_BG_GRAY4,
    //     INPUT_BORDER_COLOR: COLOR.CUSTOM_BG_GRAY3,
    //     INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND_VIOLET1,
    //     INPUT_BORDER_RADIUS: 2,
    // },
    [PALETTE_IDS.STAKE_INPUT]: {
        STAKE_INPUT_BG: COLOR.CUSTOM_BG_GRAY4,
        STAKE_INPUT_BORDER_COLOR: '#D9D9D9', //'#404149',
        STAKE_INPUT_FOCUSED_BORDER_COLOR: '#D9D9D9',
        STAKE_INPUT_BORDER_RADIUS: 2,
        STAKE_INPUT_TEXT: COLOR.BRAND_TEXT_DARK_GRAY, //'#5E5E71',
    },
    [PALETTE_IDS.PRIMARY_BUTTON]: {
        PRIMARY_BUTTON_DISABLED_BG: '#DADAE5',
    },
    [PALETTE_IDS.PRIMARY_LINK]: {
        PRIMARY_LINK_BORDER_COLOR: COLOR.BRAND_BLUE1,
        PRIMARY_LINK_BORDER_RADIUS: 2,
        PRIMARY_LINK_ICON_COLOR: COLOR.SVG_PURPLE_PINK_GRADIENT,
    },
    [PALETTE_IDS.SECONDARY_LINK]: {
        SECONDARY_LINK_TEXT: {
            fontWeight: FONT.WEIGHT_THIN,
            color: COLOR.BRAND_VIOLET2,
        },
    },
    [PALETTE_IDS.TOGGLE_BUTTON]: {
        TOGGLE_BUTTON_BORDER_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.PRIMARY_DROPDOWN]: {
        PRIMARY_DROPDOWN_BG: COLOR.CUSTOM_BG_GRAY2,
    },
    [PALETTE_IDS.INFO_NOTIFICATION]: {
        INFO_BG: COLOR.BRAND_DARK_GRAY,
    },
    [PALETTE_IDS.SUCCESS_NOTIFICATION]: {
        SUCCESS_BG: '#8dc572',
        SUCCESS_BORDER: {
            BORDER_COLOR: '#8dc572',
        },
        SUCCESS_ICON_COLOR: COLOR.BRAND_DARK_GRAY,
    },
    [PALETTE_IDS.WARNING_NOTIFICATION]: {
        WARNING_BG: '#f0ad4e',
        WARNING_BORDER: {
            BORDER_COLOR: '#f0ad4e',
        },
    },
    [PALETTE_IDS.ERROR_NOTIFICATION]: {
        ERROR_BG: '#be6464',
        ERROR_BORDER: {
            BORDER_COLOR: '#be6464',
        },
    },
    [PALETTE_IDS.BONUS_INDICATOR]: {
        BONUS_INDICATOR_BORDER_COLOR: COLOR.BRAND_VIOLET1,
        BONUS_INDICATOR_ICON_COLOR: COLOR.SVG_PURPLE_PINK_GRADIENT,
        BONUS_INDICATOR_TEXT: COLOR.BRAND_WHITE,
    },
    [PALETTE_IDS.ODDS_BOOST_BANNER]: {
        ODDS_BOOSTS_CONTAINER_BORDER_RADIUS: 0,
        ODDS_BOOSTS_ACCENT_COLOR: COLOR.BRAND_VIOLET1,
        ODDS_BOOSTS_COMMON_COLOR: COLOR.BRAND_VIOLET1,
        ODDS_BOOSTS_RETURNS_TEXT: COLOR.BRAND_WHITE,
        ODDS_BOOSTS_ACTION_BUTTON_TEXT: {
            color: COLOR.BRAND_VIOLET2,
            textTransform: 'uppercase',
        },
    },

    // Betslip and Betting Center ============================

    [PALETTE_IDS.BETTING_CENTER]: {
        BETTING_CENTER_TAB_BUTTON_TEXT: {
            textTransform: 'uppercase',
        },
        BETTING_CENTER_TAB_BUTTON_BG: COLOR.CUSTOM_BG_GRAY1,
    },
    [PALETTE_IDS.BET_HISTORY]: {
        BET_HISTORY_MOBILE_ITEM_SEPARATOR_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.BETSLIP]: {
        SLIDING_BETSLIP_SWIPER_COLOR: COLOR.BRAND_WHITE,
        BETSLIP_BET_HIGHLIGHT_INDICATOR_COLOR: '#dd3054',
        BETSLIP_BET_TYPE_SELECTOR: {
            PIVOT_TEXT: {
                fontSize: FONT.SIZE_SMALL,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
            },
            PIVOT_SELECTED_TEXT: {},
        },
        BETSLIP_BB_BET_CONTAINER_BG: COLOR.CUSTOM_BG_GRAY2,
        BETSLIP_PILL_OUTLINE: {
            BORDER_COLOR: COLOR.BRAND_VIOLET1,
            BORDER_WIDTH: 2,
        },
    },
    [PALETTE_IDS.CHANGED_VALUE_INDICATOR]: {
        CVI_NEW_VALUE_BG: COLOR.GRADIENT,
        CVI_INDICATION_ICON_COLOR: COLOR.BRAND_ORANGE,
    },
    [PALETTE_IDS.NUMERIC_KEYPAD]: {
        KEYPAD_BUTTON: {
            TOGGLE_BUTTON_BG: COLOR.CUSTOM_BG_GRAY1,
            TOGGLE_BUTTON_BORDER_COLOR: 'transparent',
            TOGGLE_BUTTON_TEXT: {
                fontSize: 15,
            },
        },
        KEYPAD_COMPACT_BUTTON: {
            TOGGLE_BUTTON_BG: COLOR.CUSTOM_BG_GRAY1,
            TOGGLE_BUTTON_BORDER_COLOR: 'transparent',
            TOGGLE_BUTTON_TEXT: {
                fontSize: 15,
            },
        },
        KEYPAD_INCREASE_BUTTON_BG: COLOR.CUSTOM_BG_GRAY4,
    },
    [PALETTE_IDS.BET_BUILDER_SELECTION_ITEM]: {
        BB_BET_PATH_LINE_COLOR: COLOR.BRAND_BLUE1,
    },

    [PALETTE_IDS.HOME_PAGE]: {
        HOME_PAGE_TOP_SPORTS_SPORT_TEXT: {fontWeight: FONT.WEIGHT_BOLD},
        HOME_PAGE_TOP_SPORTS_CONTAINER_BG: 'transparent',
        HOME_PAGE_PROMOTION_QUICK_LINKS: {
            PROMOTION_QUICK_LINK: {
                PRIMARY_LINK_BG: 'transparent',
                PRIMARY_LINK_BORDER_COLOR: COLOR.BRAND_VIOLET1,
                PRIMARY_LINK_BORDER_RADIUS: 2,
                PRIMARY_LINK_ICON_COLOR: 'transparent',
                PRIMARY_LINK_TEXT: {
                    fontWeight: FONT.WEIGHT_NORMAL,
                    fontSize: 14,
                },
            },
        },
    },
    [PALETTE_IDS.COUPONS]: {
        COUPONS_TITLE_TEXT: {
            fontWeight: FONT.WEIGHT_BOLD,
            fontSize: 16,
            color: COLOR.BRAND_WHITE,
            textTransform: 'capitalize',
        },
        TOP_SPORTS_SPORT_SELECTOR_CONTAINER_BG: COLOR.CUSTOM_BG_GRAY1,
        COUPONS_BORDER: {
            BORDER_RADIUS: 2,
            BORDER_SHOW_SHADOW: false,
        },
        PRICE_BOOST_COUPON_ITEM: {
            PBI_ODDS_BUTTON: {
                ODDS_BG: COLOR.GRADIENT_ACCENT,
                ODDS_HOVER_BG: 'linear-gradient(90deg, #f2ae3a 0%, #d437c5 100%)',
                ODDS_SELECTED_BG: 'linear-gradient(90deg, #cc8b1d 0%, #99178d 100%)',
                ODDS_INFO_TEXT: '#fff',
                ODDS_PRICE_TEXT: '#fff',
            },
        },
    },
    [PALETTE_IDS.EVENT_LIST]: {
        EVENT_LIST_BG: '#2a282d',
        EVENT_LIST_GROUP_MARKET_TEXT: {
            fontSize: 9,
            textTransform: 'uppercase',
        },
        EVENT_LIST_ALL_EVENTS_LINK: {
            SECONDARY_LINK_TEXT: {
                color: COLOR.BRAND_WHITE,
                textDecoration: 'underline',
            },
        },
        EVENT_LIST_ITEM_SEPARATOR_COLOR: COLOR.BRAND_VIOLET1,
        EVENT_LIST_GAME_LINE_ITEM_DETAILS: {
            EVENT_DETAILS_PARTICIPANTS: {
                PARTICIPANTS_TEXT: {
                    lineHeight: 1.29,
                },
                PITCHER_TEXT: {
                    lineHeight: 1.17,
                },
            },
        },
    },
    [PALETTE_IDS.EVENT_LIST_HEADER]: {
        EVENT_LIST_HEADER_SEPARATOR_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.EVENT_CARDS]: {
        CARD_EVENT_NAME_TEXT: {
            lineHeight: '16px',
        },
        CARD_TIMING_STATUS: {
            EVENT_TIMING_LIVE_TEXT: {
                lineHeight: '16px',
            },
            EVENT_TIMING_UPCOMING_TEXT: {
                lineHeight: '16px',
            },
        },
        EVENT_CARDS_ODDS_BUTTON: {
            ODDS_INFO_TEXT: {
                color: COLOR.BRAND_TEXT_GRAY,
                textTransform: 'none',
            },
        },
    },
    [PALETTE_IDS.BET_SHARING_DIALOG]: {
        BET_SHARING_DIALOG_CONTAINER_BG: COLOR.BRAND_DARK_GRAY,
        BET_SHARING_CONTENT_CONTAINER_BG: COLOR.BRAND_DARK_GRAY,
        BET_SHARING_BET_LEGS_CONTAINER_BG: COLOR.BRAND_BLACK,
    },
    [PALETTE_IDS.EVENT_PAGE]: {
        SCOREBOARD: {
            SCOREBOARD_SEGMENTED_ACTIVE_SCORE_TEXT: {
                color: COLOR.BRAND_WHITE,
                fontWeight: FONT.WEIGHT_NORMAL,
            },
        },
    },
    [PALETTE_IDS.SPORT_PAGE]: {
        LEAGUES_SECTION_HEADER_TEXT: {
            color: COLOR.BRAND_WHITE,
            fontWeight: FONT.WEIGHT_BOLD,
            fontSize: 16,
            textTransform: 'none',
        },
        LEAGUES_CONTAINER_BORDER: {
            BORDER_SHOW_SHADOW: false,
        },
        COMPETITIONS_PROMOTION_QUICK_LINKS: {
            PROMOTION_QUICK_LINK: {
                PRIMARY_LINK_TEXT: {fontSize: FONT.SIZE_MEDIUM},
            },
        },
    },
    // Interface Elements ============================
    [PALETTE_IDS.APPLICATION_HEADER]: {
        SEARCH_RESULT: {
            SEARCH_RECENT_ITEM_BG: COLOR.BRAND_DARK_GRAY,
        },
    },
    [PALETTE_IDS.MENUS_LINKS_LISTS]: {
        LHM_CONTAINER_BG: 'transparent',
        LHM_HEADER_BG: 'transparent',
        LHM_HEADER_TEXT: {
            color: COLOR.BRAND_WHITE,
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 16,
        },
    },
    [PALETTE_IDS.MOBILE_NAVBAR]: {
        // CONTAINER_BG: COLOR.BRAND_BLACK,
        CONTAINER_BORDER_RADIUS: 10,
    },
    [PALETTE_IDS.EVENT_TIMING_STATUS]: {
        EVENT_TIMING_UPCOMING_TEXT: COLOR.BRAND_WHITE,
    },
    [PALETTE_IDS.MINI_SCOREBOARD]: {
        MINI_SCOREBOARD_SCORE_COUNT_TEXT: COLOR.BRAND_WHITE,
    },

    [PALETTE_IDS.CARDS_AND_PROMOTIONS]: {
        CARD_CONTAINER_BG: COLOR.GRADIENT_GRAY,
        CARD_ODDS_BUTTON: {
            ODDS_PRICE_TEXT: {
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
            },
            ODDS_SELECTED_PRICE_TEXT: '#242226',
            ODDS_INFO_TEXT: {
                color: '#fff',
                textTransform: 'uppercase',
                fontWeight: FONT.WEIGHT_NORMAL,
                fontSize: FONT.SIZE_XSMALL,
            },
            ODDS_SELECTED_INFO_TEXT: '#242226',
        },
        CARD_WATERMARK_ICON_HIDDEN: true,
        CARD_WATERMARK_ICON_COLOR: COLOR.BRAND_VIOLET1,
    },
    [PALETTE_IDS.PRE_BUILT_CARDS]: {
        // PREBUILT_CARD_CONTAINER_BG: COLOR.BRAND_DARK_GRAY,
        PREBUILT_CARD_CONTAINER_BG: `linear-gradient(to right bottom, #414149, #414149)`, // same color to override parent gradient
        PREBUILT_CARD_ODDS_BUTTON: {
            ODDS_PRICE_TEXT: {
                fontSize: FONT.SIZE_LARGE,
            },
            ODDS_SELECTED_PRICE_TEXT: {
                fontSize: FONT.SIZE_LARGE,
            },
        },
        CARD_PREBUILT_ACCA_SELECTION_BB_BET_MARKET_TEXT: {
            fontWeight: FONT.WEIGHT_NORMAL,
        },
    },
    [PALETTE_IDS.PRICE_BOOST_BANNER]: {
        PRICE_BOOST_BANNER_CONTAINER_BG: COLOR.GRADIENT_ACCENT,
        PRICE_BOOST_BANNER_BOOST_LABEL: {
            PRICE_BOOST_LABEL_TEXT: {
                color: '#fff',
                textTransform: 'uppercase',
            },
            PRICE_BOOST_LABEL_ICON_COLOR: '#fff',
        },
        PRICE_BOOST_BANNER_BOOST_ITEM: {
            PBI_PREVIOUS_PRICE_HEADER_TEXT: {
                color: '#fff',
                fontWeight: FONT.WEIGHT_NORMAL,
                fontSize: FONT.SIZE_XSMALL,
                textTransform: 'uppercase',
            },
            PBI_PREVIOUS_PRICE_TEXT: {
                color: '#fff',
                fontWeight: FONT.WEIGHT_NORMAL,
                fontSize: FONT.SIZE_XSMALL,
                textTransform: 'uppercase',
            },
            PBI_PREVIOUS_PRICE_INDICATION_ICON_COLOR: '#fff',
            PBI_ODDS_BUTTON: {
                ODDS_BG: '#fff',
                ODDS_PRICE_TEXT: {
                    color: COLOR.BRAND_VIOLET1,
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_LARGE,
                },
                ODDS_SELECTED_PRICE_TEXT: {
                    fontWeight: FONT.WEIGHT_BOLD,
                    fontSize: FONT.SIZE_LARGE,
                },
            },
        },
    },
    [PALETTE_IDS.PRICE_BOOST_LABEL]: {
        PRICE_BOOST_LABEL_TEXT: {
            fontStyle: 'normal',
        },
    },
    [PALETTE_IDS.PRICE_BOOST_ITEM]: {
        PBI_PREVIOUS_PRICE_INDICATION_ICON_COLOR: COLOR.BRAND_ORANGE,
    },
    [PALETTE_IDS.FAVORITE_ICON]: {
        FAVORITE_ICON_TOGGLED_COLOR: COLOR.SVG_PURPLE_PINK_GRADIENT,
    },
    [PALETTE_IDS.MINI_EVENT_ITEM]: {
        MINI_EVENT_ITEM_SELECTED_CONTENT: {
            MINI_EVENT_ITEM_FAVORITE_ICON: {
                FAVORITE_ICON_TOGGLED_COLOR: COLOR.BRAND_WHITE,
            },
        },
    },
    [PALETTE_IDS.AZ_MENU]: {
        DESKTOP_AZ_EXPAND_BUTTON: {
            TERTIARY_BUTTON_TEXT: {
                color: COLOR.BRAND_VIOLET2,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
                textDecoration: 'underline',
            },
        },
        MOBILE_AZ_CONTAINER_BG: COLOR.BRAND_BLACK,
        MOBILE_AZ_SECTION_HEADER_TEXT: COLOR.BRAND_WHITE,
        MOBILE_AZ_ALL_SPORTS_BUTTON: {
            TERTIARY_BUTTON_TEXT: {
                color: COLOR.BRAND_VIOLET2,
                fontWeight: FONT.WEIGHT_NORMAL,
                textTransform: 'uppercase',
            },
        },
    },
    [PALETTE_IDS.PROMOTION_QUICK_LINKS]: {
        PROMOTION_QUICK_LINK: {
            PRIMARY_LINK_ICON_COLOR: COLOR.BRAND_WHITE,
            PRIMARY_LINK_TEXT: {
                _mobile_fontWeight: FONT.WEIGHT_BOLD,
            },
        },
    },
    [PALETTE_IDS.SCOREBOARD]: {
        SCOREBOARD_ACTIVE_SCORE_TEXT: COLOR.BRAND_WHITE,
    },
    [PALETTE_IDS.PROMOTIONS]: {
        BANNER_CONTAINER_BG: 'transparent',
    },
    [PALETTE_IDS.BAD_ROUTE_PAGE]: {
        BAD_ROUTE_PAGE_BG: COLOR.CUSTOM_BG_GRAY1,
        BAD_ROUTE_PAGE_INFO_CONTAINER_BG: 'rgba(153, 153, 153, 0.3)',
        BAD_ROUTE_PAGE_TITLE_TEXT: {
            color: COLOR.BRAND_BLUE1,
            fontSize: '32px',
            fontWeight: FONT.WEIGHT_BOLD,
        },
        BAD_ROUTE_PAGE_INFO_TEXT: {
            fontSize: FONT.SIZE_MEDIUM,
        },
        BAD_ROUTE_PAGE_HOME_BUTTON: {
            PRIMARY_BUTTON_BG: 'transparent',
            PRIMARY_BUTTON_TEXT: {
                color: COLOR.BRAND_BLUE1,
                fontSize: FONT.SIZE_LARGE,
                fontWeight: FONT.WEIGHT_BOLD,
                textDecoration: 'underline',
            },
        },
    },
    [PALETTE_IDS.MARKETS]: {
        MARKETS_CONTAINER_BG: '#2a282d',
        MARKETS_GAME_LINE_PARTICIPANTS: {
            PARTICIPANTS_TEXT: {
                lineHeight: 1.29,
            },
            PITCHER_TEXT: {
                lineHeight: 1.17,
            },
        },
        MARKETS_SELECTION_HEADER_LABEL_TEXT: {
            fontWeight: FONT.WEIGHT_SEMI_BOLD,
            fontSize: 9,
            textTransform: 'uppercase',
        },
        MARKETS_PRICE_BOOST_LABEL: {
            PRICE_BOOST_LABEL_ICON_COLOR: COLOR.BRAND_WHITE,
        },
        MARKETS_PRICE_BOOST_ITEM: {
            PBI_ODDS_BUTTON: {
                ODDS_BG: COLOR.GRADIENT_ACCENT,
                ODDS_HOVER_BG: 'linear-gradient(90deg, #f2ae3a 0%, #d437c5 100%)',
                ODDS_SELECTED_BG: 'linear-gradient(90deg, #cc8b1d 0%, #99178d 100%)',
                ODDS_INFO_TEXT: '#fff',
                ODDS_PRICE_TEXT: '#fff',
            },
        },
    },

    statscore: {
        // Config for statscore DBX feature
        livematchProConfigurationId: '69c51da2ba0ae7o5ao2b06ad', // dark theme statscore widget
        prematchProConfigurationId: '69c532db54e9e65db45e4723',
        languagesMapping: {
            'es-MX': 'es',
        },
    },
    mojitoConfiguration: {
        services: {
            configurations: {
                BaseEventCard: {
                    container: {
                        style: {
                            backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-guitar-watermark-dark.png')}), ${COLOR.GRADIENT_GRAY}`,
                            backgroundPosition: 'right bottom',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                        },
                    },
                },
                // MatchEventCard: {
                //     eventCard: {
                //         container: {
                //             style: {
                //                 backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-events-watermark-dark.png')}), ${COLOR.GRADIENT_GRAY}`,
                //                 backgroundPosition: 'right top',
                //                 backgroundSize: '100%',
                //             },
                //         },
                //     },
                // },
                SelectionCard: {
                    genericEventCard: {
                        container: {
                            style: {
                                backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-events-watermark-dark.png')}), ${COLOR.GRADIENT_GRAY}`,
                                backgroundPosition: 'right top',
                                backgroundSize: '100%',
                            },
                        },
                        clickableArea: {
                            style: {
                                base: {
                                    paddingBottom: 5,
                                },
                            },
                        },
                    },
                    boostedEventCard: {
                        container: {
                            style: {
                                backgroundColor: '#34343a',
                                backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-odds-boost-watermark-dark.png')})`,
                                backgroundSize: '35%',
                                backgroundPosition: 'right top',
                            },
                        },
                        clickableArea: {
                            style: {
                                base: {
                                    paddingBottom: 5,
                                },
                            },
                        },
                    },
                    headerContainer: {
                        style: {
                            height: 'auto',
                        },
                    },
                    selectionInfo: {
                        style: {
                            justifyContent: 'center',
                        },
                    },
                },
                PrebuiltBet: {
                    genericEventCard: {
                        container: {
                            style: {
                                backgroundImage: `url(${absoluteUrlToInternalResource('/images/generic/event-card-bet-builder-watermark-dark.png')}), ${COLOR.GRADIENT_GRAY}`,
                                backgroundPosition: '50% bottom',
                                backgroundSize: 'cover',
                            },
                        },
                    },
                },
                SportPage: {
                    pageHeader: {
                        backgroundImage: {
                            src: 'sport-page/generic_dark.png',
                            _mobile_src: 'sport-page/mobile/generic_dark.png',
                        },
                    },
                    sportBackgroundImages: [],
                    _mobile_sportBackgroundImages: [],
                },
                InPlayPage: {
                    pageHeader: {
                        headerContainer: {
                            style: {
                                backgroundColor: 'transparent',
                            },
                        },
                        label: {
                            style: {
                                color: COLOR.BRAND_WHITE,
                                fontSize: FONT.SIZE_LARGE,
                                fontWeight: FONT.WEIGHT_BOLD,
                                paddingLeft: 8,
                            },
                        },
                        icon: {
                            src: null,
                        },
                    },
                    pageTitle: {
                        text: {
                            style: {
                                color: COLOR.BRAND_WHITE,
                                fontSize: FONT.SIZE_LARGE,
                                fontWeight: FONT.WEIGHT_BOLD,
                            },
                        },
                        iconButtonContainer: {
                            style: {
                                display: 'none',
                            },
                        },
                        icon: {
                            buttonIcon: {
                                src: null,
                            },
                        },
                    },
                },
                InPlayMenu: {
                    rootContainer: {
                        style: {
                            padding: '0 0 8px 0',
                            _mobile_padding: '0 8px',
                        },
                    },
                },
                EventList: {
                    eventItem: {
                        gameLineMarketContent: {
                            market: {
                                selectionButton: {
                                    view: {
                                        background: {
                                            inactive: COLOR.BRAND_DARK_GRAY,
                                            disabled: COLOR.BRAND_DARK_GRAY,
                                        },
                                    },
                                },
                            },
                        },
                        genericMarketContent: {
                            market: {
                                selectionButton: {
                                    view: {
                                        background: {
                                            inactive: COLOR.BRAND_DARK_GRAY,
                                            disabled: COLOR.BRAND_DARK_GRAY,
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                CompetitionsView: {
                    allLeagues: {
                        sportGroupsListView: {
                            locationContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                            listContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                        },
                    },
                    topLeagues: {
                        sportGroupsListView: {
                            locationContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                            listContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                        },
                    },
                },
                OutrightsView: {
                    allLeagues: {
                        sportGroupsListView: {
                            locationContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                            listContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                        },
                    },
                    topLeagues: {
                        sportGroupsListView: {
                            locationContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                            listContainer: {
                                style: {
                                    backgroundColor: '#2a282d',
                                },
                            },
                        },
                    },
                },
                Market: {
                    selectionButton: {
                        view: {
                            background: {
                                inactive: COLOR.BRAND_DARK_GRAY,
                                disabled: COLOR.BRAND_DARK_GRAY,
                            },
                        },
                    },
                },
                CorrectScoreMarketContent: {
                    selectionButton: {
                        view: {
                            background: {
                                inactive: COLOR.BRAND_DARK_GRAY,
                                disabled: COLOR.BRAND_DARK_GRAY,
                            },
                        },
                    },
                },
                ShareBetContent: {
                    logo: {
                        src: `generic/logo-dark.svg`,
                    },
                },
                ShareBetslip: {
                    logo: {
                        src: `generic/logo-dark.svg`,
                    },
                },
                // only for standalone
                ApplicationMenu: {
                    logo: {
                        src: `generic/logo-dark.svg`,
                    },
                },
                PriceBoostPageView: {
                    pageTitle: {
                        container: {
                            style: {
                                ...resolveBackground(COLOR.GRADIENT_ACCENT),
                            },
                        },
                    },
                },
                PriceBoostAggregatedMarket: {
                    titleContainer: {
                        style: {
                            ...resolveBackground(COLOR.GRADIENT_ACCENT),
                        },
                    },
                    titleText: {
                        style: {
                            padding: 0,
                            fontWeight: FONT.WEIGHT_NORMAL,
                        },
                    },
                },
            },
        },
    },
};
